import { untypedIsEnabled } from '@ip-only/client-features';
import {
  CHANGE_ORDER_REQUEST_APP_COUNTRIES,
  CONSTRUCTION_DOCUMENTS_APP_COUNTRIES,
  DELIVERY_OVERVIEW_APP_COUNTRIES,
  HOME_APP_COUNTRIES,
  INSTALLATION_REPORTING_APP_COUNTRIES,
  LAND_AGREEMENTS_APP_COUNTRIES,
  LAND_AGREEMENTS_APP_COUNTRIES_V2,
  LAND_SURVEYS_APP_COUNTRIES,
  NETWORK_IMPORT_APP_COUNTRIES,
  ROLLOUT_PROGRESS_APP_COUNTRIES,
  SPAN_PROTECTION_APP_COUNTRIES,
  USERS_APP_COUNTRIES,
} from '../apps-deploy-countries-def/apps-countries';
import {
  getTopLevelDomain,
  isDomainLocalhost,
  isTopLevelDomainFI,
} from '../shared/util/deploy-helper';
import {
  CONSTRUCTION_DOCUMENTS_ROLES,
  CONTRACTOR_USER_ADMIN_ROLES,
  DELIVERY_OVERVIEW_ROLES,
  DELIVERY_PORTAL_BETA_USER,
  LAND_AGREEMENT_ROLES,
  LAND_SURVEY_ROLES,
  NETWORK_IMPORT_ROLES,
  SPAN_PROTECTION_ROLES,
  SPAN_ROLES_VIEW,
  WORK_VARIATION_ROLES,
} from '../user-roles-def/user-roles';
import { RolesCondition } from '../user/user';
import { UserService } from '../user/user.service';
import { SideMenuLink } from './menus/side-menu/side-menu.component';

interface ButtonInputAccessData {
  roles?: { roleList: string[]; roleCondition: RolesCondition };
  featureFlag?: string;
  buttonInput: SideMenuLink;
  countryFlags: string[];
  rolesFI?: { roleList: string[]; roleCondition: RolesCondition };
}

const homePage: ButtonInputAccessData = {
  buttonInput: {
    icon: 'house',
    label: 'homeLabel',
    url: '/home',
  },
  countryFlags: HOME_APP_COUNTRIES,
};

const usersPage: ButtonInputAccessData = {
  roles: {
    roleList: CONTRACTOR_USER_ADMIN_ROLES,
    roleCondition: 'or',
  },
  buttonInput: {
    icon: 'users',
    label: 'usersLabel',
    url: '/users',
  },
  countryFlags: USERS_APP_COUNTRIES,
};

const landSurveyPage: ButtonInputAccessData = {
  roles: {
    roleList: LAND_SURVEY_ROLES,
    roleCondition: 'or',
  },
  buttonInput: {
    icon: 'expand',
    label: 'landSurveyLabel',
    url: '/land-survey',
  },

  countryFlags: LAND_SURVEYS_APP_COUNTRIES,
};

const spanPage: ButtonInputAccessData = {
  roles: {
    roleList: SPAN_ROLES_VIEW,
    roleCondition: 'or',
  },
  buttonInput: {
    icon: 'locationPin',
    label: 'rolloutProgressLabel',
    url: '/rollout-progress',
  },

  countryFlags: ROLLOUT_PROGRESS_APP_COUNTRIES,
};

const landAgreementPage: ButtonInputAccessData = {
  roles: {
    roleList: LAND_AGREEMENT_ROLES,
    roleCondition: 'or',
  },
  buttonInput: {
    icon: 'fileLines',
    label: 'landAgreementLabel',
    url: '/land-agreement',
  },

  countryFlags: LAND_AGREEMENTS_APP_COUNTRIES,
};

const landAgreementPageV2: ButtonInputAccessData = {
  roles: {
    roleList: LAND_AGREEMENT_ROLES,
    roleCondition: 'or',
  },
  rolesFI: {
    roleList: [DELIVERY_PORTAL_BETA_USER],
    roleCondition: 'or',
  },
  buttonInput: {
    icon: 'fileLines',
    label: 'landAgreementV2Label',
    url: '/land-agreement-v2',
  },

  countryFlags: LAND_AGREEMENTS_APP_COUNTRIES_V2,
};

const workVariationPage: ButtonInputAccessData = {
  roles: {
    roleList: WORK_VARIATION_ROLES,
    roleCondition: 'or',
  },
  buttonInput: {
    icon: 'dashboard',
    label: 'workVariationLabel',
    url: '/work-variation',
  },

  countryFlags: CHANGE_ORDER_REQUEST_APP_COUNTRIES,
};

const NetworkImportPage: ButtonInputAccessData = {
  roles: {
    roleList: NETWORK_IMPORT_ROLES,
    roleCondition: 'or',
  },
  buttonInput: {
    icon: 'plugConnected',
    label: 'networkImportLabel',
    url: '/network-import',
  },
  featureFlag: 'networkImport',

  countryFlags: NETWORK_IMPORT_APP_COUNTRIES,
  rolesFI: {
    roleList: [DELIVERY_PORTAL_BETA_USER],
    roleCondition: 'or',
  },
};

const InstallationReportingPage: ButtonInputAccessData = {
  roles: {
    roleList: SPAN_ROLES_VIEW,
    roleCondition: 'or',
  },
  buttonInput: {
    icon: 'receipt',
    label: 'installationReportingLabel',
    url: '/installation-reporting',
  },
  countryFlags: INSTALLATION_REPORTING_APP_COUNTRIES,
};

const DeliveryOverviewPage: ButtonInputAccessData = {
  roles: {
    roleList: DELIVERY_OVERVIEW_ROLES,
    roleCondition: 'or',
  },
  buttonInput: {
    icon: 'graph',
    label: 'deliveryOverviewLabel',
    url: '/delivery-overview',
  },
  countryFlags: DELIVERY_OVERVIEW_APP_COUNTRIES,
};

const ConstructionDocumentsPage: ButtonInputAccessData = {
  roles: {
    roleList: CONSTRUCTION_DOCUMENTS_ROLES,
    roleCondition: 'or',
  },
  buttonInput: {
    icon: 'filePdf',
    label: 'constructionDocumentsLabel',
    url: '/construction-documents',
  },
  countryFlags: CONSTRUCTION_DOCUMENTS_APP_COUNTRIES,
};

const SpanProtectionPage: ButtonInputAccessData = {
  roles: {
    roleList: SPAN_PROTECTION_ROLES,
    roleCondition: 'or',
  },
  buttonInput: {
    icon: 'shieldCheck',
    label: 'spanProtectionLabel',
    url: '/span-protection',
  },
  featureFlag: 'spanProtection',
  countryFlags: SPAN_PROTECTION_APP_COUNTRIES,
};

const pageList: ButtonInputAccessData[] = [
  homePage,
  usersPage,
  DeliveryOverviewPage,
  ConstructionDocumentsPage,
  landAgreementPage,
  landAgreementPageV2,
  spanPage,
  InstallationReportingPage,
  workVariationPage,
  landSurveyPage,
  NetworkImportPage,
  SpanProtectionPage,
];

export const buildSideMenuLinks = (
  userService: UserService,
): SideMenuLink[] => {
  return pageList
    .filter((page) => {
      let topLevelDomain = getTopLevelDomain();
      if (topLevelDomain === undefined && isDomainLocalhost()) {
        topLevelDomain = 'se';
      }
      if (!page.roles) {
        return page;
      }
      if (
        page.countryFlags.includes(topLevelDomain) &&
        userService.checkAccess(
          page.roles.roleList,
          page.roles.roleCondition,
        ) &&
        checkFIRoles(userService, page) &&
        (!page.featureFlag || untypedIsEnabled(page.featureFlag))
      ) {
        return page;
      }
    })
    .map((page) => page.buttonInput);
};

export const checkFIRoles = (
  userService: UserService,
  page: ButtonInputAccessData,
) => {
  if (
    isTopLevelDomainFI() &&
    page.countryFlags.includes('fi') &&
    page.rolesFI
  ) {
    return userService.checkAccess(
      page.rolesFI.roleList,
      page.roles?.roleCondition,
    );
  }
  return true;
};
